import EventEmitter from 'eventemitter3';
import Element from '../UI/Element';
import { TextAlignment } from '../UI/TextAlignment';
import {
    IRewardData,
    RewardType,
    getRewardData,
} from '../../Utility/RewardHelper';
import { luckyTurnipLootToRewardData } from '../../State/LuckyTurnips/LuckyTurnipsSaga';
import { luckyTurnipsLoot } from '../../Constants/Chests';
import { cached } from '../../Utility/async';
import { MathHelper } from '../../Utility/MathHelper';
import { ArrayHelper } from '../../Utility/ArrayHelper';
import { report } from '../../report';
import GameObject, { GameObjectEvent } from '../../GameObject/GameObject';
import Game from '../../Game';
import Point from '../../Utility/Point';
import { LuckyTurnipsHUD } from './LuckyTurnipsHUD';
import Sprite from '../Sprite';
import Container from '../Container';
import Interactable from '../../Component/Interactable';
import { STAR_CHEST_TIER_DATA } from '../../Constants/Chests';
import Text from '../Text';
import { DeferredRead } from '../../Utility/Deferred';
import AnimationClip from '../../System/Animation/AnimationClip';
import {
    FlyingTurnip,
    finalScale,
} from '../../System/Animation/Clip/FlyingTurnip';
import { ShowTurnipReward } from '../../System/Animation/Clip/ShowTurnipReward';
import AnimationFrame from '../../Component/AnimationFrame';
import { scaleSpriteToScreen } from 'Utility/LevelHelper';

enum UIEvent {
    PickTurnip = 'pickTurnip',
    Claim = 'claim',
}

export class LuckyTurnipsUI extends Element {
    private readonly events = new EventEmitter();

    private playAnimation: (clip: AnimationClip<unknown>) => DeferredRead<void>;

    private hud: LuckyTurnipsHUD;
    private turnips = new Array<{
        turnip: GameObject;
        reward: GameObject;
        shine: AnimationFrame;
        scale: number;
        isHighReward: boolean;
    }>();
    private turnipsInChest: Array<number>;
    private turnipFinalParent: Sprite;
    private starChestTier: number;
    private picksAllowed: number;

    private animateTurnipPromises: Array<DeferredRead<void>>;
    private hovers: Map<number, boolean>;
    private wrapper: GameObject<PIXI.Sprite>;

    public create(params: {
        starChestTier: number;
        lootOrder: ReadonlyArray<number>;
        picksAllowed: number;
        pickedTurnipIndices: ReadonlyArray<number>;
        playAnimation: LuckyTurnipsUI['playAnimation'];
        highestUnlockedLevel: number;
    }) {
        this.picksAllowed = params.picksAllowed;
        this.starChestTier = params.starChestTier;
        this.playAnimation = params.playAnimation;
        this.animateTurnipPromises = [];
        this.hovers = new Map();
        super.create({ name: 'MyLuckyTurnipsUI' });
        this.turnipsInChest = [...params.pickedTurnipIndices];

        const background = this.createBackground();

        this.wrapper = GameObject.Instantiate(Sprite, {
            anchor: new Point(0.5, 0.5),
            relativePosition: new Point(0.5, 0.5),
        });

        this.addChild(background);
        this.addChild(this.wrapper);

        const { chestBack, chestFront } = this.createGameChestParts(
            { x: 500, y: 70 },
            params.starChestTier
        );
        this.turnipFinalParent = chestBack;
        this.wrapper.addChild(chestBack);
        this.wrapper.addChild(chestFront);

        this.turnips = params.lootOrder.map((lootIndex, turnipIndex) => {
            const position = getTurnipSpawnPosition(turnipIndex);
            const reward = luckyTurnipLootToRewardData(
                luckyTurnipsLoot[lootIndex],
                params.highestUnlockedLevel
            );
            const crop = this.createCrop(
                turnipIndex,
                position,
                reward,
                luckyTurnipsLoot[lootIndex].isHighReward
            );
            crop.turnip.on(GameObjectEvent.SELECTED, () => {
                if (this.animateTurnipPromises.some(d => !d.isSettled()))
                    return;
                this.hovers.set(turnipIndex, false);
                this.updateInteractivity();
                crop.turnip.off(GameObjectEvent.POINTER_OVER);
                crop.turnip.off(GameObjectEvent.POINTER_OUT);
                this.events.emit(UIEvent.PickTurnip, turnipIndex);
            });
            crop.turnip.on(GameObjectEvent.POINTER_OVER, () => {
                this.hovers.set(turnipIndex, true);
                this.updateInteractivity();
            });
            crop.turnip.on(GameObjectEvent.POINTER_OUT, () => {
                this.hovers.set(turnipIndex, false);
                this.updateInteractivity();
            });
            this.wrapper.addChild(crop.container);
            return {
                turnip: crop.turnip,
                reward: crop.reward,
                shine: crop.shine,
                scale: position.scale,
                isHighReward: luckyTurnipsLoot[lootIndex].isHighReward,
            };
        });

        this.hud = GameObject.Instantiate(LuckyTurnipsHUD, {
            picksLeft: this.picksAllowed - params.pickedTurnipIndices.length,
            displayRestoreContextMessage: params.pickedTurnipIndices.length > 0,
        });
        this.addChild(this.hud);

        this.hud.addClaimClickListener(() => {
            this.events.emit(UIEvent.Claim);
        });

        return;

        function getTurnipSpawnPosition(turnipIndex: number) {
            const result = [
                { x: MathHelper.lerp(-405, 340, 4 / 4), y: -100, scale: 0.85 },
                { x: MathHelper.lerp(-405, 340, 3 / 4), y: -100, scale: 0.85 },
                { x: MathHelper.lerp(-405, 340, 2 / 4), y: -100, scale: 0.85 },
                { x: MathHelper.lerp(-405, 340, 1 / 4), y: -100, scale: 0.85 },
                { x: MathHelper.lerp(-405, 340, 0 / 4), y: -100, scale: 0.85 },

                { x: MathHelper.lerp(-470, 240, 4 / 4), y: 40, scale: 0.9 },
                { x: MathHelper.lerp(-470, 240, 3 / 4), y: 40, scale: 0.9 },
                { x: MathHelper.lerp(-470, 240, 2 / 4), y: 40, scale: 0.9 },
                { x: MathHelper.lerp(-470, 240, 1 / 4), y: 40, scale: 0.9 },
                { x: MathHelper.lerp(-470, 240, 0 / 4), y: 40, scale: 0.9 },

                { x: MathHelper.lerp(-490, 260, 4 / 4), y: 185, scale: 1 },
                { x: MathHelper.lerp(-490, 260, 3 / 4), y: 185, scale: 1 },
                { x: MathHelper.lerp(-490, 260, 2 / 4), y: 185, scale: 1 },
                { x: MathHelper.lerp(-490, 260, 1 / 4), y: 185, scale: 1 },
                { x: MathHelper.lerp(-490, 260, 0 / 4), y: 185, scale: 1 },
            ][turnipIndex];
            if (!result) {
                report('Unexpected turnip index', {
                    once: true,
                    tags: { turnipIndex },
                });
                return { x: 0, y: 0, scale: 1 };
            }
            const assetScale = 0.85;
            return {
                x: result.x / assetScale,
                y: result.y / assetScale,
                scale: result.scale * assetScale,
            };
        }
    }

    // Background

    private createBackground() {
        const luckyTurnipsBg = GameObject.Instantiate(Sprite, {
            name: 'backgroundSprite',
            texture: 'luckyturnips_bg',
            anchor: new Point(0.5, 0.5),
            relativePosition: new Point(0.5, 0.5),
        });
        scaleSpriteToScreen(luckyTurnipsBg);
        return luckyTurnipsBg;
    }

    // Game layer

    private createGameChestParts(
        position: { x: number; y: number },
        starChestTier: number
    ) {
        const pair = this.createSpritePair(
            'chest_back',
            'chest_front',
            `luckyturnips_chest${starChestTier}_back`,
            `luckyturnips_chest${starChestTier}_front`,
            position,
            STAR_CHEST_TIER_DATA[starChestTier].backFrontOffset[0],
            STAR_CHEST_TIER_DATA[starChestTier].backFrontOffset[1]
        );

        return {
            chestBack: pair[0],
            chestFront: pair[1],
        };
    }

    private createSpritePair(
        nameBack: string,
        nameFront: string,
        textureBack: string,
        textureFront: string,
        position: { x: number; y: number },
        offsetX: number,
        offsetY: number
    ) {
        const back = GameObject.Instantiate(Sprite, {
            name: nameBack,
            texture: textureBack,
            anchor: new Point(0.5, 0.5),
            x: position.x + offsetX,
            y: position.y + offsetY,
        });

        const front = GameObject.Instantiate(Sprite, {
            name: nameFront,
            texture: textureFront,
            anchor: new Point(0.5, 0.5),
            x: position.x,
            y: position.y,
        });

        return [back, front];
    }

    private createCrop(
        index: number,
        position: { x: number; y: number; scale: number },
        rewardData: IRewardData,
        isHighReward: boolean
    ) {
        const container = GameObject.Instantiate(Container, {
            name: 'crop_container',
        });
        container.container.scale.x = position.scale;
        container.container.scale.y = position.scale;
        const dirtPair = this.createSpritePair(
            'dirt_back',
            'dirt_front',
            'luckyturnips_ground_back',
            'luckyturnips_ground_front',
            position,
            0,
            -40
        );

        container.addChild(dirtPair[0]);

        // reward
        const shine = GameObject.Instantiate(AnimationFrame, {
            name: 'shiny',
            resourceName: 'LuckyTurnips',
            animationName: isHighReward
                ? 'luckyturnips_effect0'
                : 'luckyturnips_effect1',
            autoStart: false,
            anchor: Point.Center,
        });
        shine.spriteComponent.alpha = 0;
        shine.spriteComponent.container.x = position.x;
        shine.spriteComponent.container.y = position.y - 50;
        container.addChild(shine);

        const reward = this.createRewardUI(
            rewardData,
            position,
            this.turnipsInChest.includes(index)
        );
        container.addChild(reward);

        // Container for turnip
        // Used to reparent back and forth without worrying about z-order
        // Also used to not worry about position for turnip hover effect
        const turnipContainer = GameObject.Instantiate(Container, {
            name: 'turnip_container',
            x: position.x,
            y: position.y - 32,
        });
        container.addChild(turnipContainer);
        // turnip
        const turnip = GameObject.Instantiate(Sprite, {
            name: `turnip_${index}`,
            texture: 'luckyturnips_turnip',
            anchor: new Point(0.5, 0.66),
        });
        turnip.addComponent(Interactable, { enabled: true, buttonMode: true });
        if (this.turnipsInChest.includes(index)) {
            turnip.getComponent(Interactable).enabled = false;
            this.turnipFinalParent.addChild(turnip);
            const xy = this.getTurnipTargetGlobalPosition(
                this.turnipsInChest.indexOf(index)
            );
            turnip.container.position.set(xy.x, xy.y);
            turnip.container.scale.set(finalScale, finalScale);
        } else {
            turnipContainer.addChild(turnip);
        }

        container.addChild(dirtPair[1]);

        return {
            container,
            turnip,
            reward,
            shine,
        };
    }

    private createRewardUI(
        reward: IRewardData,
        position: { x: number; y: number },
        alreadyPicked: boolean
    ) {
        const rewardSpriteData = getRewardData(
            // Assuming reward.type matches what getRewardData supports
            reward.type as Parameters<typeof getRewardData>[0]
        );
        const rewardSprite = GameObject.Instantiate(Sprite, {
            name: 'reward',
            texture: rewardSpriteData.texture,
            anchor: new Point(0.5, 0.5),
            x: position.x,
            y: position.y - 50,
            alpha: alreadyPicked ? 1 : 0,
        });

        const offsetY: Partial<Record<RewardType, number>> = {
            [RewardType.UNDO]: -60,
            [RewardType.PLUS_FIVE_CARDS]: -75,
            [RewardType.JOKER]: -75,
            [RewardType.FREE_PLAY]: -65,
            [RewardType.COINS]: -80,
        };
        const rewardText = GameObject.Instantiate(Text, {
            name: 'text',
            text: `x${reward.value}`,
            style: {
                fontFamily: 'FrancoisOne',
                fontSize: 35,
                fill: '#fcffd9',
                align: TextAlignment.CENTER,
            },
            anchor: new Point(0.5, 0.5),
            x: 0,
            y: offsetY[reward.type] ?? -80,
        });

        rewardSprite.addChild(rewardText);
        if (reward.type === RewardType.COINS) {
            rewardSprite.container.scale = new Point(0.85, 0.85);
        }

        return rewardSprite;
    }

    // Public part

    public onTurnipClick(listener: (turnipIndex: number) => void) {
        this.events.on(UIEvent.PickTurnip, (turnipIndex: number) => {
            listener(turnipIndex);
        });
    }

    public onClaimClick(listener: () => void) {
        this.events.on(UIEvent.Claim, listener);
    }

    public animateTurnipPick(index: number) {
        const { turnip, reward, shine, scale, isHighReward } = this.turnips[
            index
        ];

        let parentScale = scale;

        const turnipsInChest = this.turnipsInChest.length;
        this.animateTurnipPromises.push(
            this.playAnimation.call(
                null,
                new FlyingTurnip(turnip, {
                    getTargetPosition: (parentScale: number) =>
                        getGlobalPosition(
                            turnip.container,
                            this.getTurnipTargetGlobalPosition(turnipsInChest),
                            parentScale
                        ),
                    rewardObject: reward,
                    shinyObject: shine,
                    isHighReward,
                    parentScale,
                    finalParent: this.turnipFinalParent,
                })
            )
        );

        this.turnipsInChest.push(index);
        this.hud.updatePicksLeft(
            this.picksAllowed - this.turnipsInChest.length
        );

        this.updateInteractivity();
        awaitAllDynamic(this.animateTurnipPromises).then(
            this.updateInteractivity
        );
    }

    private readonly updateInteractivity = () => {
        this.turnips.forEach(({ turnip }, turnipIndex) => {
            const animatingSomeTurnip = this.animateTurnipPromises.some(
                d => !d.isSettled()
            );
            const turnipInChest = this.turnipsInChest.includes(turnipIndex);

            const inputEnabled =
                !animatingSomeTurnip &&
                !turnipInChest &&
                this.turnipsInChest.length < this.picksAllowed;

            // also affects `buttonMode`
            turnip.getComponent(Interactable).enabled = inputEnabled;

            if (!turnipInChest) {
                turnip.container.position.y =
                    inputEnabled && this.hovers.get(turnipIndex) ? -10 : 0;
            }
        });
    };

    private getTurnipTargetGlobalPosition(turnipsInChest: number) {
        const data = STAR_CHEST_TIER_DATA[this.starChestTier];
        const [x, y] = data.luckyTurnipPicks[turnipsInChest];
        return {
            x: this.turnipFinalParent.container.worldTransform.tx + x,
            y: this.turnipFinalParent.container.worldTransform.ty + y,
        };
    }

    public readonly revealAll = cached(async () => {
        await awaitAllDynamic(this.animateTurnipPromises);

        const luckyTurnipState = Game.store.getState().playerState
            .luckyTurnipsState;

        const remainingLootIndices = luckyTurnipState.lootOrder.filter(
            lootIndex => !luckyTurnipState.pickedLootIndices.includes(lootIndex)
        );
        ArrayHelper.shuffle(remainingLootIndices);
        await Promise.all(
            remainingLootIndices.map(lootIndex => {
                const turnipIndex = luckyTurnipState.lootOrder.indexOf(
                    lootIndex
                );
                const { turnip, reward, shine, isHighReward } = this.turnips[
                    turnipIndex
                ];

                return this.playAnimation.call(
                    null,
                    new ShowTurnipReward(reward, {
                        rewardObject: reward,
                        shinyObject: shine,
                        objectToHide: turnip,
                        isHighReward,
                        revealingAll: true,
                    })
                ).promise;
            })
        );
        this.hud.setCollectAllVisibleTo(true);
    });

    public *animateClaimRewards(): IterableIterator<number> {
        this.hud.setCollectAllVisibleTo(false);
    }
}

async function awaitAllDynamic(dynamicArray: ReadonlyArray<DeferredRead>) {
    const finished = new Array<DeferredRead>();
    while (true) {
        const toWaitFor = dynamicArray.filter(e => !finished.includes(e));
        if (toWaitFor.length === 0) return;
        await Promise.all(toWaitFor.map(e => e.promise));
        finished.push(...toWaitFor);
    }
}

function getGlobalPosition(
    object: PIXI.Container,
    position: { x: number; y: number },
    parentScale: number
) {
    // FIXME: consider pivot and scale
    return {
        x: (position.x - object.parent.worldTransform.tx) / parentScale,
        y: (position.y - object.parent.worldTransform.ty) / parentScale,
    };
}
